import React from "react";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { useTranslation, useLandingTranslation } from "@tablemint/i18n";
import { useUser, useAuth, useSession } from "@clerk/clerk-react";
import { createCheckoutSession } from "../services/stripe";
import {
  Loader2,
  CheckCircle,
  AlertCircle,
  X,
  HelpCircle,
  ChevronRight,
  Copy,
  Check,
} from "lucide-react";
import AnimatedSection from "../components/animations/AnimatedSection";
import { fadeInUp, popIn, staggerContainer } from "../utils/animation";
import { motion } from "framer-motion";

// Hardcoded subscription plans
const PLANS = [
  {
    id: "starter",
    name: "Starter",
    description: "Perfect for small restaurants just getting started",
    monthlyPrice: 29,
    yearlyPrice: 290, // ~17% discount
    features: [
      { name: "features.basic_widget", included: true },
      { name: "features.reservations_50", included: true },
      { name: "features.email_notifications", included: true },
      { name: "features.basic_customer_management", included: true },
      { name: "features.single_staff_account", included: true },
      { name: "features.table_management", included: false },
      { name: "features.sms_notifications", included: false },
      { name: "features.custom_branding", included: false },
      { name: "features.multiple_staff", included: false },
      { name: "features.analytics", included: false },
    ],
    priceId: {
      monthly: "price_starter_monthly",
      yearly: "price_starter_yearly",
    },
    popular: false,
  },
  {
    id: "growth",
    name: "Growth",
    description: "For restaurants focused on expansion and efficiency",
    monthlyPrice: 79,
    yearlyPrice: 790, // ~17% discount
    features: [
      { name: "features.basic_widget", included: true },
      { name: "features.unlimited_reservations", included: true },
      { name: "features.email_notifications", included: true },
      { name: "features.advanced_customer_management", included: true },
      { name: "features.five_staff_accounts", included: true },
      { name: "features.table_management", included: true },
      { name: "features.sms_notifications", included: true },
      { name: "features.custom_branding", included: false },
      { name: "features.api_access", included: false },
      { name: "features.priority_support", included: false },
    ],
    priceId: {
      monthly: "price_growth_monthly",
      yearly: "price_growth_yearly",
    },
    popular: true,
  },
  {
    id: "premium",
    name: "Premium",
    description: "The ultimate solution for established restaurants",
    monthlyPrice: 149,
    yearlyPrice: 1490, // ~17% discount
    features: [
      { name: "features.advanced_widget", included: true },
      { name: "features.unlimited_reservations", included: true },
      { name: "features.email_sms_notifications", included: true },
      { name: "features.complete_customer_management", included: true },
      { name: "features.unlimited_staff", included: true },
      { name: "features.advanced_table_management", included: true },
      { name: "features.analytics", included: true },
      { name: "features.white_label", included: true },
      { name: "features.api_access", included: true },
      { name: "features.dedicated_account_manager", included: true },
    ],
    priceId: {
      monthly: "price_premium_monthly",
      yearly: "price_premium_yearly",
    },
    popular: false,
  },
];

// Features comparison section data
const FEATURE_CATEGORIES = [
  {
    category: "categories.reservations",
    features: [
      {
        name: "features.monthly_reservations",
        starter: "50",
        growth: "Unlimited",
        premium: "Unlimited",
      },
      {
        name: "features.table_management",
        starter: "Basic",
        growth: "Advanced",
        premium: "Advanced",
      },
      {
        name: "features.waitlist_management",
        starter: false,
        growth: true,
        premium: true,
      },
      {
        name: "features.no_show_tools",
        starter: false,
        growth: true,
        premium: true,
      },
    ],
  },
  {
    category: "categories.customer_management",
    features: [
      {
        name: "features.guest_profiles",
        starter: "Basic",
        growth: "Advanced",
        premium: "Complete",
      },
      {
        name: "features.dining_preferences",
        starter: false,
        growth: true,
        premium: true,
      },
      {
        name: "features.vip_flagging",
        starter: false,
        growth: true,
        premium: true,
      },
      {
        name: "features.reservation_history",
        starter: "30 days",
        growth: "1 year",
        premium: "Unlimited",
      },
    ],
  },
  {
    category: "categories.notifications",
    features: [
      {
        name: "features.email_confirmations",
        starter: true,
        growth: true,
        premium: true,
      },
      {
        name: "features.sms_notifications",
        starter: false,
        growth: true,
        premium: true,
      },
      {
        name: "features.custom_email_templates",
        starter: false,
        growth: true,
        premium: true,
      },
      {
        name: "features.automated_reminders",
        starter: false,
        growth: true,
        premium: true,
      },
    ],
  },
  {
    category: "categories.support",
    features: [
      {
        name: "features.customer_support",
        starter: "Email",
        growth: "Email & Chat",
        premium: "Priority",
      },
      {
        name: "features.account_manager",
        starter: false,
        growth: false,
        premium: true,
      },
      {
        name: "features.setup_assistance",
        starter: false,
        growth: true,
        premium: true,
      },
      {
        name: "features.staff_training",
        starter: false,
        growth: "Basic",
        premium: "Advanced",
      },
    ],
  },
];

export default function Pricing() {
  const { t: tLanding } = useLandingTranslation();
  const { t: tCommon } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isSignedIn } = useUser();
  const { session } = useSession();

  const [yearlyBilling, setYearlyBilling] = useState<boolean>(false);
  const [checkoutLoading, setCheckoutLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [showComparison, setShowComparison] = useState<boolean>(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  // Get query params
  const queryParams = new URLSearchParams(location.search);
  const checkoutStatus = queryParams.get("checkout");

  // Handle checkout status from query params
  useEffect(() => {
    if (checkoutStatus === "success") {
      setMessage({
        type: "success",
        text: tCommon(
          "subscription.checkoutSuccess",
          "Your subscription has been activated successfully!"
        ),
      });
    } else if (checkoutStatus === "canceled") {
      setMessage({
        type: "error",
        text: tCommon(
          "subscription.checkoutCanceled",
          "Your checkout was canceled. Feel free to try again when you're ready."
        ),
      });
    }
  }, [checkoutStatus, tCommon]);

  const handleCheckout = async (plan: (typeof PLANS)[0]) => {
    if (!isSignedIn) {
      navigate("/login?redirect=/pricing");
      return;
    }

    setSelectedPlanId(plan.id);
    setCheckoutLoading(true);
    setMessage(null); // Clear any existing messages

    try {
      // Get session token for auth
      const token = await session?.getToken();

      if (!token) {
        console.error("No auth token available");
        throw new Error("Authentication failed");
      }

      const priceId = yearlyBilling
        ? plan.priceId.yearly
        : plan.priceId.monthly;

      // Add more debugging to identify potential issues
      console.log("Creating checkout session with:", {
        priceId,
        planId: plan.id,
        successUrl: `${window.location.origin}/dashboard?checkout=success`,
        cancelUrl: `${window.location.origin}/pricing?checkout=canceled`,
      });

      // Create checkout session
      const { url } = await createCheckoutSession(
        priceId,
        `${window.location.origin}/dashboard?checkout=success`,
        `${window.location.origin}/pricing?checkout=canceled`,
        token
      );

      if (!url) {
        throw new Error("No checkout URL returned from server");
      }

      window.location.href = url;
    } catch (error: any) {
      console.error("Error creating checkout session:", error);

      // Provide more specific error message based on the error type
      let errorMessage = tCommon(
        "subscription.checkoutError",
        "There was an error creating your checkout session. Please try again."
      );

      if (error.message === "Authentication failed") {
        errorMessage = tCommon(
          "subscription.authError",
          "Authentication failed. Please sign in again and try once more."
        );
      } else if (error.response && error.response.status === 401) {
        errorMessage = tCommon(
          "subscription.sessionExpired",
          "Your session has expired. Please sign in again."
        );
      } else if (error.response && error.response.status === 500) {
        errorMessage = tCommon(
          "subscription.systemError",
          "Our payment system is experiencing issues. Please try again later."
        );
      }

      setMessage({
        type: "error",
        text: errorMessage,
      });
    } finally {
      setCheckoutLoading(false);
    }
  };

  const renderSavingsBadge = (plan: (typeof PLANS)[0]) => {
    const monthlyTotal = plan.monthlyPrice * 12;
    const yearlySavings = monthlyTotal - plan.yearlyPrice;
    const savingsPercent = Math.round((yearlySavings / monthlyTotal) * 100);

    return yearlyBilling ? (
      <Badge
        variant="outline"
        className="absolute top-2 right-2 bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800"
      >
        {tLanding("pricing.annualDiscount", {
          percent: savingsPercent.toString(),
          defaultValue: `Save ${savingsPercent}%`,
        })}
      </Badge>
    ) : null;
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    setTimeout(() => {
      setCopiedCode(null);
    }, 2000);
  };

  // Generate embed code and link examples
  const generateEmbedCode = (plan: string) => {
    return `<script>
(function(d, s, id) {
  var js, tjs = d.getElementsByTagName(s)[0];
  if (d.getElementById(id)) return;
  js = d.createElement(s); js.id = id;
  js.src = "https://tablemint.com/widget.js";
  js.setAttribute("data-plan", "${plan}");
  tjs.parentNode.insertBefore(js, tjs);
}(document, "script", "tablemint-widget"));
</script>`;
  };

  const generateDirectLink = (plan: string) => {
    return `https://tablemint.com/signup?plan=${plan}`;
  };

  // Add a checkStripeStatus function to verify Stripe configuration
  const checkStripeStatus = async () => {
    setCheckoutLoading(true);
    try {
      const response = await fetch("/api/stripe/status", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to check Stripe status");
      }

      const data = await response.json();
      console.log("Stripe configuration status:", data);

      // You could display this information to admin users
      if (data.status === "error") {
        setMessage({
          type: "error",
          text: tCommon(
            "subscription.stripeConfigError",
            "Stripe integration is not properly configured. Please contact support."
          ),
        });
      }
    } catch (error) {
      console.error("Error checking Stripe status:", error);
    } finally {
      setCheckoutLoading(false);
    }
  };

  // Call this function in development mode to help debug
  useEffect(() => {
    if (process.env.NODE_ENV === "development") {
      // Uncomment the line below when actively debugging Stripe issues
      // checkStripeStatus();
    }
  }, []);

  // Localized plan data
  const getLocalizedPlanInfo = (planId: string) => {
    // Find the plan
    const plan = PLANS.find((p) => p.id === planId);

    return {
      name: tLanding(`pricing.${planId}.name`, plan?.name || ""),
      description: tLanding(
        `pricing.${planId}.description`,
        plan?.description || ""
      ),
      cta: tLanding(
        `pricing.${planId}.cta`,
        planId === "premium" ? "Contact Sales" : "Subscribe"
      ),
      // Add currency symbol
      currency: tCommon("currency.symbol", "€"),
    };
  };

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-white to-primary/5 dark:from-gray-900 dark:to-primary/10">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-primary/10 dark:bg-primary/20"
          animate={{ y: [0, 15, 0], opacity: [0.5, 0.8, 0.5] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute top-1/3 -left-24 w-64 h-64 rounded-full bg-blue-500/10 dark:bg-blue-500/20"
          animate={{ y: [0, -20, 0], opacity: [0.3, 0.6, 0.3] }}
          transition={{
            duration: 7,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1,
          }}
        />
        <motion.div
          className="absolute bottom-0 right-1/4 w-64 h-64 rounded-full bg-purple-500/10 dark:bg-purple-500/20"
          animate={{ y: [0, 10, 0], opacity: [0.2, 0.5, 0.2] }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.5,
          }}
        />
      </div>

      <div className="container mx-auto py-24 px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          className="text-center mb-16"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <motion.h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-300"
            variants={fadeInUp}
          >
            {tLanding("pricing.title", "Simple, Transparent Pricing")}
          </motion.h1>
          <motion.p
            className="text-xl md:text-2xl mb-10 max-w-3xl mx-auto text-gray-600 dark:text-gray-300"
            variants={fadeInUp}
          >
            {tLanding(
              "pricing.subtitle",
              "Choose the perfect plan for your restaurant. No hidden fees."
            )}
          </motion.p>

          {/* Billing toggle */}
          <motion.div
            variants={fadeInUp}
            className="flex items-center justify-center mt-8 bg-white/80 dark:bg-gray-800/60 backdrop-blur-sm p-4 rounded-full inline-flex mx-auto shadow-md border border-gray-100 dark:border-gray-700"
          >
            <span
              className={`mr-3 ${!yearlyBilling ? "font-medium text-gray-900 dark:text-white" : "text-gray-500 dark:text-gray-400"}`}
            >
              {tLanding("pricing.monthly", "Monthly")}
            </span>
            <Switch
              checked={yearlyBilling}
              onCheckedChange={setYearlyBilling}
              aria-label="Toggle yearly billing"
              className="data-[state=checked]:bg-primary data-[state=unchecked]:bg-gray-200"
            />
            <span
              className={`ml-3 ${yearlyBilling ? "font-medium text-gray-900 dark:text-white" : "text-gray-500 dark:text-gray-400"} `}
            >
              {tLanding("pricing.annually", "Yearly")}
              <span className="ml-1 text-primary font-medium">
                (
                {tLanding("pricing.annualDiscount", {
                  percent: "17",
                })}
                )
              </span>
            </span>
          </motion.div>
        </motion.div>

        {/* Status message */}
        {message && (
          <AnimatedSection className="mb-12" variants={fadeInUp}>
            <div
              className={`p-6 rounded-xl flex items-start mx-auto max-w-3xl shadow-md ${
                message.type === "success"
                  ? "bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-300 border border-green-200 dark:border-green-800/30"
                  : "bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-300 border border-red-200 dark:border-red-800/30"
              }`}
            >
              <div className="flex-shrink-0 mr-3">
                {message.type === "success" ? (
                  <CheckCircle className="h-6 w-6" />
                ) : (
                  <AlertCircle className="h-6 w-6" />
                )}
              </div>
              <div>
                <p className="font-medium mb-1">{message.text}</p>
                {message.type === "error" && (
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {tCommon(
                      "subscription.errorHelp",
                      "If this error persists, please contact our support team at support@tablemint.com or try refreshing the page."
                    )}
                  </p>
                )}
              </div>
            </div>
          </AnimatedSection>
        )}

        {/* Pricing cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {PLANS.map((plan) => {
            const localizedPlan = getLocalizedPlanInfo(plan.id);
            return (
              <AnimatedSection
                key={plan.id}
                className="h-full"
                variants={popIn}
                delay={
                  plan.id === "starter" ? 0 : plan.id === "growth" ? 0.1 : 0.2
                }
              >
                <Card
                  className={`flex flex-col h-full relative overflow-hidden transition-all duration-300 border-2 ${
                    plan.popular
                      ? "border-primary shadow-xl shadow-primary/20 transform hover:scale-105"
                      : "hover:border-primary/50 hover:shadow-lg hover:scale-[1.02]"
                  } backdrop-blur-sm bg-white/90 dark:bg-gray-800/80`}
                >
                  {plan.popular && (
                    <div className="absolute top-0 right-0 w-36 bg-primary text-white px-4 py-1 text-sm transform translate-x-[32%] translate-y-[0%] rotate-45 shadow-sm z-10 font-medium">
                      {tCommon("subscription.popular", "Most Popular")}
                    </div>
                  )}

                  {renderSavingsBadge(plan)}

                  <CardHeader
                    className={`pb-4 ${plan.popular ? "bg-primary/10 dark:bg-primary/20" : ""}`}
                  >
                    <CardTitle className="text-2xl flex items-center">
                      {localizedPlan.name}
                    </CardTitle>
                    <CardDescription className="min-h-[50px]">
                      {localizedPlan.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex-grow pt-6">
                    <div className="flex items-baseline mb-6">
                      <span className="text-5xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-300">
                        {localizedPlan.currency}
                        {yearlyBilling ? plan.yearlyPrice : plan.monthlyPrice}
                      </span>
                      <span className="ml-2 text-gray-500 dark:text-gray-400">
                        /
                        {yearlyBilling
                          ? tCommon("time.year", "year")
                          : tCommon("time.month", "month")}
                      </span>
                    </div>

                    {yearlyBilling && (
                      <div className="text-sm px-3 py-1.5 mb-6 inline-flex bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400 rounded-md">
                        <span>
                          {tLanding("pricing.billedAnnually", {
                            amount: `${localizedPlan.currency}${Math.round(plan.yearlyPrice / 12)}`,
                          })}
                        </span>
                      </div>
                    )}

                    <ul className="mt-6 space-y-3">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-start">
                          {feature.included ? (
                            <CheckCircle className="h-5 w-5 text-green-500 dark:text-green-400 mr-2 flex-shrink-0 mt-0.5" />
                          ) : (
                            <X className="h-5 w-5 text-gray-300 dark:text-gray-600 mr-2 flex-shrink-0 mt-0.5" />
                          )}
                          <span
                            className={
                              feature.included
                                ? ""
                                : "text-gray-400 dark:text-gray-500"
                            }
                          >
                            {tCommon(
                              feature.name,
                              feature.name
                                .replace("features.", "")
                                .replace(/_/g, " ")
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                  <CardFooter className="pt-2">
                    <Button
                      onClick={() => handleCheckout(plan)}
                      className={`w-full ${
                        plan.popular
                          ? "bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20 transform transition-all duration-300 hover:translate-y-[-2px]"
                          : "bg-gray-700 hover:bg-gray-600 text-white dark:bg-gray-700 dark:hover:bg-gray-600"
                      }`}
                      disabled={checkoutLoading}
                    >
                      {checkoutLoading && selectedPlanId === plan.id ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : null}
                      {localizedPlan.cta}
                      <ChevronRight className="ml-2 h-4 w-4" />
                    </Button>
                  </CardFooter>
                </Card>
              </AnimatedSection>
            );
          })}
        </div>

        {/* Toggle comparison button */}
        <div className="text-center mb-12">
          <Button
            variant="outline"
            onClick={() => setShowComparison(!showComparison)}
            className="mx-auto bg-white/80 dark:bg-gray-800/60 backdrop-blur-sm hover:bg-white dark:hover:bg-gray-700/60 transform transition-all duration-300 hover:scale-105 shadow-md border-primary/20 hover:border-primary/40"
          >
            {showComparison
              ? tLanding("pricing.compare.hide", "Hide comparison")
              : tLanding("pricing.compare.title", "Compare all features")}
            <HelpCircle className="ml-2 h-4 w-4" />
          </Button>
        </div>

        {/* Feature comparison table */}
        {showComparison && (
          <AnimatedSection className="mb-16" variants={fadeInUp}>
            <div className="overflow-x-auto rounded-xl border border-gray-100 dark:border-gray-700 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm shadow-lg">
              <div className="min-w-[768px]">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-900/50">
                      <th className="text-left p-4 border-b dark:border-gray-700">
                        {tLanding("pricing.compare.feature", "Feature")}
                      </th>
                      <th className="text-center p-4 border-b dark:border-gray-700">
                        {tLanding("pricing.starter.name", "Starter")}
                      </th>
                      <th className="text-center p-4 border-b dark:border-gray-700 bg-primary/5 dark:bg-primary/10">
                        {tLanding("pricing.growth.name", "Growth")}
                      </th>
                      <th className="text-center p-4 border-b dark:border-gray-700">
                        {tLanding("pricing.premium.name", "Premium")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {FEATURE_CATEGORIES.map((category, categoryIndex) => (
                      <React.Fragment key={categoryIndex}>
                        <tr className="bg-gray-50/50 dark:bg-gray-900/30">
                          <td
                            colSpan={4}
                            className="p-4 font-medium border-b dark:border-gray-800"
                          >
                            {tCommon(
                              category.category,
                              category.category
                                .replace("categories.", "")
                                .replace(/_/g, " ")
                            )}
                          </td>
                        </tr>
                        {category.features.map((feature, featureIndex) => (
                          <tr
                            key={`${categoryIndex}-${featureIndex}`}
                            className="border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50"
                          >
                            <td className="p-4">
                              {tCommon(
                                feature.name,
                                feature.name
                                  .replace("features.", "")
                                  .replace(/_/g, " ")
                              )}
                            </td>
                            <td className="p-4 text-center">
                              {typeof feature.starter === "boolean" ? (
                                feature.starter ? (
                                  <CheckCircle className="h-5 w-5 text-green-500 dark:text-green-400 mx-auto" />
                                ) : (
                                  <X className="h-5 w-5 text-gray-300 dark:text-gray-600 mx-auto" />
                                )
                              ) : (
                                tCommon(
                                  `${feature.name}.starter`,
                                  feature.starter
                                )
                              )}
                            </td>
                            <td className="p-4 text-center bg-primary/5 dark:bg-primary/10">
                              {typeof feature.growth === "boolean" ? (
                                feature.growth ? (
                                  <CheckCircle className="h-5 w-5 text-green-500 dark:text-green-400 mx-auto" />
                                ) : (
                                  <X className="h-5 w-5 text-gray-300 dark:text-gray-600 mx-auto" />
                                )
                              ) : (
                                tCommon(
                                  `${feature.name}.growth`,
                                  feature.growth
                                )
                              )}
                            </td>
                            <td className="p-4 text-center">
                              {typeof feature.premium === "boolean" ? (
                                feature.premium ? (
                                  <CheckCircle className="h-5 w-5 text-green-500 dark:text-green-400 mx-auto" />
                                ) : (
                                  <X className="h-5 w-5 text-gray-300 dark:text-gray-600 mx-auto" />
                                )
                              ) : (
                                tCommon(
                                  `${feature.name}.premium`,
                                  feature.premium
                                )
                              )}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </AnimatedSection>
        )}

        {/* FAQ Section */}
        <AnimatedSection
          className="max-w-3xl mx-auto py-12 px-6 rounded-2xl bg-white/80 dark:bg-gray-800/60 backdrop-blur-sm shadow-lg border border-gray-100 dark:border-gray-700"
          variants={fadeInUp}
        >
          <h2 className="text-3xl font-bold mb-8 text-center bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-300">
            {tLanding("pricing.faq.title", "Frequently Asked Questions")}
          </h2>
          <div className="space-y-6">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300">
              <h3 className="font-semibold text-lg mb-2">
                {tLanding(
                  "pricing.faq.changePlansQuestion",
                  "Can I change my plan later?"
                )}
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                {tLanding(
                  "pricing.faq.changePlansAnswer",
                  "Yes, you can upgrade or downgrade your plan at any time. The changes will take effect on your next billing cycle."
                )}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300">
              <h3 className="font-semibold text-lg mb-2">
                {tLanding(
                  "pricing.faq.trialQuestion",
                  "Do you offer a free trial?"
                )}
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                {tLanding(
                  "pricing.faq.trialAnswer",
                  "We offer a 14-day free trial on all plans with no credit card required to get started."
                )}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300">
              <h3 className="font-semibold text-lg mb-2">
                {tLanding(
                  "pricing.faq.paymentQuestion",
                  "How does billing work?"
                )}
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                {tLanding(
                  "pricing.faq.billingAnswer",
                  "You'll be billed either monthly or annually, depending on your preference. Annual plans receive a 17% discount compared to monthly billing."
                )}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300">
              <h3 className="font-semibold text-lg mb-2">
                {tLanding(
                  "pricing.faq.cancelQuestion",
                  "Can I cancel my subscription?"
                )}
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                {tLanding(
                  "pricing.faq.cancelAnswer",
                  "Yes, you can cancel your subscription at any time. Your plan will remain active until the end of your current billing period."
                )}
              </p>
            </div>
          </div>
        </AnimatedSection>

        {/* CTA Section */}
        <AnimatedSection className="mt-20" variants={fadeInUp}>
          <div className="py-12 px-6 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 dark:from-primary/30 dark:to-primary/10 backdrop-blur-sm shadow-lg border border-white/20 dark:border-gray-700/30 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-[url('/images/pattern.svg')] opacity-10"></div>
            <div className="relative z-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">
                {tLanding(
                  "pricing.cta.title",
                  "Ready to transform your reservation system?"
                )}
              </h2>
              <p className="text-xl mb-8 max-w-2xl mx-auto text-gray-700 dark:text-gray-300">
                {tLanding(
                  "pricing.cta.subtitle",
                  "Join thousands of restaurants already growing their business with Tablemint."
                )}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Button
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20 transform transition-all duration-300 hover:scale-105 hover:translate-y-[-2px]"
                  onClick={() => navigate("/signup")}
                >
                  {tLanding("pricing.cta.trialButton", "Start Your Free Trial")}
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="border-primary text-primary hover:bg-primary/10"
                  onClick={() => navigate("/contact")}
                >
                  {tLanding("pricing.cta.contactButton", "Contact Sales")}
                </Button>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-4">
                {tLanding(
                  "pricing.cta.noCommitment",
                  "No commitment. Cancel anytime."
                )}
              </p>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </div>
  );
}
